/** localStorage: user completed or skipped pre-paywall onboarding (show once per browser until cleared). */
export const PRE_PAYWALL_ONBOARDING_DONE_KEY = "rareRoles.prePaywallOnboarding.v1";
/** First-visit homepage preferences gate (seen once per browser). */
export const FIRST_VISIT_PREFS_DONE_KEY = "rareRoles.firstVisitPrefs.v1";
/** Soft banner on /jobs dismissed once. */
export const FIRST_VISIT_JOBS_BANNER_DISMISSED_KEY = "rareRoles.firstVisitJobsBanner.v1";
/** Saved answers for paywall copy and future personalization. */
export const QUICK_PROFILE_KEY = "rareRoles.quickProfile.v1";

export const ROLE_FAMILY_KEYS = [
  "engineering",
  "product",
  "design",
  "data",
  "sales",
  "marketing",
  "ops_other",
];

export const SENIORITY_KEYS = ["student", "early", "mid", "senior", "lead"];

export const TIMELINE_KEYS = ["active", "passive", "browsing"];

export const WORK_MODEL_KEYS = ["us_remote", "us_hybrid_remote", "global_remote"];

export const ROLE_FAMILY_LABELS = {
  engineering: "Engineering",
  product: "Product",
  design: "Design",
  data: "Data & analytics",
  sales: "Sales & GTM",
  marketing: "Marketing",
  ops_other: "Ops & other",
};

export const SENIORITY_LABELS = {
  student: "Student / intern",
  early: "Early career (0–2 yrs)",
  mid: "Mid-level (3–5 yrs)",
  senior: "Senior (6+ yrs)",
  lead: "Lead / staff / principal",
};

export const TIMELINE_LABELS = {
  active: "Actively interviewing",
  passive: "Open, not urgent",
  browsing: "Just browsing",
};

export const WORK_MODEL_LABELS = {
  us_remote: "US · Remote only",
  us_hybrid_remote: "US · Remote or hybrid",
  global_remote: "Global · Remote",
};

export function isPrePaywallOnboardingDone() {
  try {
    return window.localStorage.getItem(PRE_PAYWALL_ONBOARDING_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markPrePaywallOnboardingDone() {
  try {
    window.localStorage.setItem(PRE_PAYWALL_ONBOARDING_DONE_KEY, "1");
  } catch {
    // ignore
  }
}

export function isFirstVisitPrefsDone() {
  try {
    return window.localStorage.getItem(FIRST_VISIT_PREFS_DONE_KEY) === "1";
  } catch {
    return false;
  }
}

export function markFirstVisitPrefsDone() {
  try {
    window.localStorage.setItem(FIRST_VISIT_PREFS_DONE_KEY, "1");
  } catch {
    // ignore
  }
}

export function isFirstVisitJobsBannerDismissed() {
  try {
    return window.localStorage.getItem(FIRST_VISIT_JOBS_BANNER_DISMISSED_KEY) === "1";
  } catch {
    return false;
  }
}

export function markFirstVisitJobsBannerDismissed() {
  try {
    window.localStorage.setItem(FIRST_VISIT_JOBS_BANNER_DISMISSED_KEY, "1");
  } catch {
    // ignore
  }
}

export function readQuickProfile() {
  try {
    const raw = window.localStorage.getItem(QUICK_PROFILE_KEY);
    if (!raw) return null;
    const p = JSON.parse(raw);
    return p && typeof p === "object" ? p : null;
  } catch {
    return null;
  }
}

/** @param {Record<string, unknown>} profile */
export function writeQuickProfile(profile) {
  try {
    window.localStorage.setItem(
      QUICK_PROFILE_KEY,
      JSON.stringify({ ...profile, updatedAt: Date.now() })
    );
  } catch {
    // ignore
  }
}

export function clearQuickProfile() {
  try {
    window.localStorage.removeItem(QUICK_PROFILE_KEY);
  } catch {
    // ignore
  }
}

/** @param {{ roleFamily?: string, roleFamilies?: string[] } | null} profile */
export function getRoleFamilyKeys(profile) {
  if (!profile || typeof profile !== "object") return [];
  if (Array.isArray(profile.roleFamilies) && profile.roleFamilies.length > 0) {
    return profile.roleFamilies.filter((k) => ROLE_FAMILY_KEYS.includes(k));
  }
  if (profile.roleFamily && ROLE_FAMILY_KEYS.includes(profile.roleFamily)) {
    return [profile.roleFamily];
  }
  return [];
}

/** Human-readable summary for paywall subcopy (e.g. "Product · Senior · US remote"). */
export function formatQuickProfileTagline(profile) {
  if (!profile || typeof profile !== "object") return "";
  const parts = [];
  const roleKeys = getRoleFamilyKeys(profile);
  if (roleKeys.length > 0) {
    parts.push(roleKeys.map((k) => ROLE_FAMILY_LABELS[k]).filter(Boolean).join(", "));
  }
  const se = profile.seniority;
  const wm = profile.workModel;
  const tl = profile.timeline;
  if (se && SENIORITY_LABELS[se]) parts.push(SENIORITY_LABELS[se]);
  if (wm && WORK_MODEL_LABELS[wm]) parts.push(WORK_MODEL_LABELS[wm]);
  if (tl && TIMELINE_LABELS[tl]) parts.push(TIMELINE_LABELS[tl]);
  return parts.join(" · ");
}
